// JBF Admin API Service - Django Backend Integration
import { get, post, patch, put, del } from "@/services/axiosUitils";

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  phone?: string;
  phone_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string;
  user_type?: string;
  created_at?: string;
  created_on?: string;
  updated_on?: string;
  is_active?: boolean;
  /** Role from login/register response (e.g. institutions_admin, hostel_admin) */
  jbf_role?: string;
  profile?: {
    jbf_role: string;
    created_at?: string;
    updated_at?: string;
  };
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
  jbf_role?: string;
}

export interface Profile {
  id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  avatar_url?: string;
  created_at?: string;
}

export interface Student {
  id: string;
  user_id: string;
  profile?: Profile;
  applications?: any[];
}

export interface StudentAdmissionInquiry {
  id: string | number;
  full_name: string;
  address: string;
  email: string;
  mobile: string;
  pincode: string;
  hostel_remark?: string | null;
  course_std_name?: string | null;
  college_school?: string | null;
  last_year_remark?: string | null;
  last_result_percentage: string;
  preferred_zone: number;
  preferred_state: number;
  preferred_cities: number[];
  preferred_areas: number[];
  auth_status: boolean;
  created_at: string;
  updated_at: string;
  submitted_by?: string | null;
  preferred_zone_name?: string;
  preferred_state_name?: string;
  preferred_cities_names?: string[];
  preferred_areas_names?: string[];
  submitted_by_email?: string;
  submitted_by_username?: string;
}

export interface StudentAdmissionInquiryResponse {
  message: string;
  status_code: number;
  data: {
    count: number;
    inquiries: StudentAdmissionInquiry[];
  };
}

export interface Hostel {
  id: string;
  name: string;
  status?: string;
  address?: string;
  created_at?: string;
}

export interface Institution {
  id: string;
  name: string;
  type?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  total_students?: number | null;
  staff_count?: number | null;
  departments?: string[] | null;
  trustee_name?: string | null;
  trustee_contact?: string | null;
  status?: string | null;
  address?: string;
  created_at?: string;
  /** Image URLs or objects from API; field name matches form upload key image */
  image?: string[] | { url: string }[] | null;
}

export interface Donor {
  id: string;
  user_id: string;
  profile?: Profile;
  donations?: any[];
}

export interface Trustee {
  id: string;
  user_id: string;
  profile?: Profile;
}

export interface Userjbf_role {
  id: string;
  user_id: string;
  jbf_role: string;
}

export interface Blog {
  id: string | number;
  title: string;
  sub_title?: string | null;
  description?: string | null;
  /** Category ID from API */
  category?: number | string | null;
  /** Category display name (from list API) */
  category_name?: string | null;
  banner?: string | null;
  date?: string | null;
  is_enabled?: boolean;
  image?: string | null;
  video?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** List blogs API response: { message, status_code, data } */
export interface BlogListResponse {
  message: string;
  status_code: number;
  data: Blog[];
}

/** Payload for creating/updating a blog (same field names as API). Use FormData when image is included. */
export interface BlogCreatePayload {
  title: string;
  sub_title?: string;
  description?: string;
  category?: string;
  date?: string;
  is_enabled?: boolean;
  image?: File | null;
  video?: string;
}

/** Blog category from GET /jbf/blog-categories/ */
export interface BlogCategory {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// FAQs (Super Admin)
// ============================================================================

export interface FAQCategory {
  id: number | string;
  title: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface FAQCategoryCreatePayload {
  title: string;
  is_enabled: boolean;
  order_by: number;
}

export interface FAQQuestion {
  id: number | string;
  category: number | string | FAQCategory;
  category_name?: string;
  question: string;
  answer: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface FAQQuestionCreatePayload {
  category: number | string;
  question: string;
  answer: string;
  is_enabled: boolean;
  order_by: number;
}

// ============================================================================
// Testimonials (Super Admin)
// ============================================================================

export interface Testimonial {
  id: number | string;
  category: string;
  name: string;
  designation: string;
  description: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface TestimonialCreatePayload {
  category: string;
  name: string;
  designation: string;
  description: string;
  is_enabled: boolean;
  order_by: number;
}

// ============================================================================
// Events (Super Admin)
// ============================================================================

export interface JBFEvent {
  id: number | string;
  event_for: string;
  address: string;
  event_title: string;
  date: string;
  number_of_days: number | string;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export interface EventCreatePayload {
  event_for: string;
  address: string;
  event_title: string;
  date: string;
  number_of_days: number | string;
  description: string;
}

/** List events API response: { message, status_code, data } */
export interface EventListResponse {
  message: string;
  status_code: number;
  data: JBFEvent[];
}

export interface BlogCategoriesResponse {
  message: string;
  status_code: number;
  data: BlogCategory[];
}

// ============================================================================
// Media Gallery (Super Admin)
// ============================================================================

export interface GalleryCategory {
  id: number | string;
  name: string;
  display_order: number;
  is_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface GalleryCategoryCreatePayload {
  name: string;
  display_order: number;
  is_enabled: boolean;
}

export interface GalleryItem {
  id: number | string;
  category: number | string;
  category_name?: string;
  title: string;
  description: string;
  year: string | number;
  display_order: number;
  image?: string;
  created_at?: string;
  updated_at?: string;
}

export interface GalleryItemCreatePayload {
  category: number | string;
  title: string;
  description: string;
  year: string | number;
  display_order: number;
  image?: File | null;
}

// ============================================================================
// Vendors (Super Admin)
// ============================================================================

export interface PastProject {
  project_name: string;
  location: string;
  year: string;
}

export interface Vendor {
  id: string | number;
  vendor_name: string;
  /** Category IDs – API may return number[] or string[] */
  vendor_category?: (string | number)[] | null;
  /** Category names for display (from API) */
  vendor_category_names?: string[] | null;
  /** Legacy / alias for vendor_category */
  category?: (string | number)[] | null;
  /** Sub-category IDs – API may return number[] or string[] */
  vendor_sub_category?: (string | number)[] | null;
  /** Sub-category names for display (from API) */
  vendor_sub_category_names?: string[] | null;
  company_name?: string | null;
  vendor_type?: "individual" | "company" | null;
  /** Logo URL – view/detail API may return as `logo` or `logo_url` */
  logo?: string | null;
  logo_url?: string | null;
  short_description?: string | null;
  contact_person_name?: string | null;
  mobile_number?: string | null;
  email?: string | null;
  office_address?: string | null;
  city?: string | null;
  state?: string | null;
  pin_code?: string | null;
  service_areas?: string[] | null;
  pan_india_service?: boolean | null;
  services_offered?: string[] | null;
  specialization?: string | null;
  years_of_experience?: number | string | null;
  hostel_projects_experience?: boolean | null;
  past_projects?: string | PastProject[] | null;
  empanelment_status?: string | null;
  verification_status?: string | null;
  documents_uploaded?: string | null;
  compliance_confirmation?: boolean | null;
  admin_notes?: string | null;
  available_for_work?: boolean | null;
  working_days?: string[] | null;
  service_timings?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface VendorCreatePayload {
  vendor_name: string;
  vendor_category?: string[];
  vendor_sub_category?: string[];
  category?: string[];
  company_name?: string;
  vendor_type?: "individual" | "company";
  /** Logo: File (upload) or string (URL) – sent as `logo_image` */
  logo_image?: File | string | null;
  short_description?: string;
  contact_person_name?: string;
  mobile_number?: string;
  email?: string;
  office_address?: string;
  city?: string;
  state?: string;
  pin_code?: string;
  service_areas?: string[];
  pan_india_service?: boolean;
  services_offered?: string[];
  specialization?: string;
  years_of_experience?: number | string;
  hostel_projects_experience?: boolean;
  /** Past projects: string (legacy) or array of { project_name, location, year } – sent as `past_projects` */
  past_projects?: string | PastProject[];
  empanelment_status?: string;
  verification_status?: string;
  compliance_confirmation?: boolean;
  admin_notes?: string;
  available_for_work?: boolean;
  working_days?: string[];
  service_timings?: string;
  /** Documents: File (upload) or string (URL) – sent as `documents_uploaded` */
  documents_uploaded?: File | string | null;
}

/** Coerce numeric string to number for backend validation (e.g. IntegerField). */
function coerceNumeric(value: string | number): number | string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const s = String(value).trim();
  if (s === "") {
    return value as string;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : value;
}

/** Coerce array for ID fields: numeric strings become numbers so backend accepts. */
function coerceIdArray(arr: string[]): (number | string)[] {
  return arr.map(v => {
    const s = String(v).trim();
    const n = Number(s);
    return s !== "" && Number.isFinite(n) ? n : v;
  });
}

// ============================================================================
// Authentication API
// ============================================================================

export const authAPI = {
  // Email/Password login
  login: async (email: string, password: string) => {
    return post<AuthResponse>("/auth/login/", { email, password });
  },

  // Signup
  signup: async (data: {
    email: string;
    password: string;
    phone?: string;
    user_type?: string;
    first_name?: string;
    last_name?: string;
  }) => {
    return post<AuthResponse>("/auth/", data);
  },

  /** Create user (super admin) - POST /auth/ with email, password, fullname, jbf_role */
  createUser: async (data: {
    email: string;
    password: string;
    full_name: string;
    jbf_role: string;
    phone_number?: string;
  }) => {
    return post<AuthResponse>("/auth/", data);
  },

  // Logout
  logout: async () => {
    return post("/auth/logout/");
  },

  // Refresh token
  refreshToken: async (refreshToken: string) => {
    return post<{ access: string }>("/auth/token-refresh/", {
      refresh: refreshToken,
    });
  },

  // Forgot password request
  forgotPasswordRequest: async (email: string) => {
    return post<{ message: string }>("/forgot-password-request/", {
      email,
    });
  },

  // Forgot password reset
  forgotPasswordReset: async (
    id: string,
    data: { new_password: string; confirm_password: string }
  ) => {
    return post(`/forgot-password-reset/${id}/`, data);
  },

  // Send OTP - Login uses /auth/login/, Register uses /auth/
  // Returns code_reference which must be used for verification
  sendOTP: async (
    phoneNumber: string,
    phoneCode: string = import.meta.env.VITE_PHONE_CODE as string,
    forRegister?: boolean
  ) => {
    const payload: Record<string, string> = {
      phone_number: phoneNumber,
      phone_code: phoneCode,
    };

    const endpoint = forRegister ? "/auth/" : "/auth/login/";
    return post<{ message: string; code_reference: string }>(endpoint, payload);
  },

  // Verify OTP - Login uses /auth/login/, Register uses /auth/
  // Returns auth tokens and user data
  verifyOTP: async (
    otp: string,
    codeReference: string,
    forRegister?: boolean,
    fullName?: string
  ) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    const payload: Record<string, string> = {
      otp: otp.trim(),
      code_reference: codeReference.trim(),
    };
    if (fullName) {
      payload.full_name = fullName;
    }

    const endpoint = forRegister ? "/auth/" : "/auth/login/";
    return post<AuthResponse>(endpoint, payload);
  },

  // Email OTP - Send OTP (alternative method if backend supports it)
  sendEmailOTP: async (email: string) => {
    return post<{ message: string; code_reference?: string }>(
      "/auth/verify-email/send-otp/",
      {
        email,
      }
    );
  },

  // Email OTP - Verify OTP
  verifyEmailOTP: async (
    email: string,
    otp: string,
    codeReference?: string
  ) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<AuthResponse>("/auth/verify-email/verify-otp/", {
      email,
      otp,
      code_reference: codeReference.trim(),
    });
  },

  // Email OTP - Resend OTP
  resendEmailOTP: async (email: string, codeReference: string) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<{ message: string; code_reference?: string }>(
      "/auth/verify-email/resend-otp/",
      {
        email,
        code_reference: codeReference.trim(),
      }
    );
  },

  // General Resend OTP (for phone-based auth)
  resendOTP: async (phone: string, codeReference: string) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<{ message: string; code_reference?: string }>(
      "/auth/resend-otp/",
      {
        phone,
        code_reference: codeReference.trim(),
      }
    );
  },

  // Phone-based OTP (if backend supports it)
  // Note: These endpoints may need to be implemented in Django backend
  sendPhoneOTP: async (phone: string, portal?: string) => {
    return post<{ message: string }>("/auth/send-otp/", {
      phone,
      portal,
    });
  },

  verifyPhoneOTP: async (phone: string, otp: string, portal?: string) => {
    return post<AuthResponse>("/auth/verify-otp/", {
      phone,
      otp,
      portal,
    });
  },

  // Get My Profile - Fetch profile based on role context (POST with jbf_role)
  getMyProfile: async (jbf_role: string) => {
    return post<User>("/jbf/my-profile/", { jbf_role });
  },

  /** GET my-profile - returns profile with has_institution (for institution login redirect) */
  getMyProfileGet: async () => {
    return get<{
      message?: string;
      status_code?: number;
      data?: {
        user_id?: string;
        user_email?: string;
        jbf_role?: string | null;
        has_institution?: boolean;
      };
    }>("/jbf/my-profile/");
  },
};

// ============================================================================
// Profiles API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const profilesAPI = {
  get: async (userId: string) => {
    return get<Profile>(`/profiles/${userId}/`);
  },

  upsert: async (data: Partial<Profile>) => {
    return post<Profile>("/profiles/", data);
  },

  update: async (userId: string, data: Partial<Profile>) => {
    return patch<Profile>(`/profiles/${userId}/`, data);
  },
};

// ============================================================================
// User jbf_roles API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const userjbf_rolesAPI = {
  getByUserId: async (userId: string) => {
    return get<any>(`/jbf/user-role/${userId}/`);
  },

  create: async (data: { user_id: string; jbf_role: string }) => {
    return post<Userjbf_role>("/user-jbf_roles/", data);
  },
};

/** Alias for consumers that expect userRolesAPI */
export const userRolesAPI = userjbf_rolesAPI;

// ============================================================================
// Super Admin – User list / CRUD (for User Management page)
// ============================================================================
/** User list item as returned by list API or built from auth + profile */
export interface UserListItem {
  id: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  jbf_role?: string;
  created_on?: string;
}

export const superAdminUsersAPI = {
  list: async (params?: { search?: string; jbf_role?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (
                value !== undefined &&
                value !== null &&
                String(value).trim() !== ""
              ) {
                acc[key] = String(value).trim();
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : "";
    return get<UserListItem[] | { data: UserListItem[] }>(
      `/jbf/users/${queryParams}`
    );
  },
  get: async (id: string) => {
    return get<UserListItem>(`/jbf/users/${id}/`);
  },
  create: async (data: {
    email: string;
    password?: string;
    full_name: string;
    jbf_role: string;
    phone_number?: string;
  }) => {
    return post<any>("/jbf/users/", data);
  },
  update: async (
    id: string,
    data: {
      full_name?: string;
      email?: string;
      jbf_role?: string;
      phone_number?: string;
    }
  ) => {
    return patch<UserListItem>(`/jbf/users/${id}/`, data);
  },
  delete: async (id: string) => {
    return del(`/jbf/users/${id}/`);
  },
};

// ============================================================================
// Student Admission Inquiry API
// ============================================================================

export const studentAdmissionInquiryAPI = {
  list: async (
    params?: PaginationParams
  ): Promise<StudentAdmissionInquiryResponse> => {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                acc[key] = String(value);
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : "";
    return get<StudentAdmissionInquiryResponse>(
      `/jbf/student-admission-inquiry/${queryParams}`
    );
  },

  getAll: async (params?: Record<string, string | number | boolean>) => {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null && `${value}` !== "") {
                acc[key] = String(value);
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : "";
    return get<StudentAdmissionInquiry[]>(
      `/jbf/student-admission-inquiry/${queryParams}`
    );
  },

  get: async (id: string | number) => {
    return get<StudentAdmissionInquiry>(
      `/jbf/student-admission-inquiry/${id}/`
    );
  },

  getByUserId: async (userId: string) => {
    return get<StudentAdmissionInquiry[]>(
      `/jbf/student-admission-inquiry/?user_id=${userId}`
    );
  },

  getMyInquiry: async () => {
    return get<StudentAdmissionInquiry[]>(
      `/jbf/student-admission-inquiry/my-inquiry/`
    );
  },

  create: async (data: Partial<StudentAdmissionInquiry>) => {
    return post<StudentAdmissionInquiry>(
      "/jbf/student-admission-inquiry/",
      data
    );
  },

  replace: async (
    id: string | number,
    data: Partial<StudentAdmissionInquiry>
  ) => {
    return put<StudentAdmissionInquiry>(
      `/jbf/student-admission-inquiry/${id}/`,
      data
    );
  },

  update: async (
    id: string | number,
    data: Partial<StudentAdmissionInquiry>
  ) => {
    return patch<StudentAdmissionInquiry>(
      `/jbf/student-admission-inquiry/${id}/`,
      data
    );
  },

  delete: async (id: string | number) => {
    return del(`/jbf/student-admission-inquiry/${id}/`);
  },
};

// ============================================================================
// Hostels API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const hostelsAPI = {
  getAll: async (params?: { status?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return get<Hostel[]>(`/hostels/${queryParams}`);
  },

  get: async (id: string) => {
    return get<Hostel>(`/hostels/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Hostel[]>(`/hostels/?user_id=${userId}`);
  },

  create: async (data: Partial<Hostel>) => {
    return post<Hostel>("/hostels/", data);
  },

  update: async (id: string, data: Partial<Hostel>) => {
    return patch<Hostel>(`/hostels/${id}/`, data);
  },

  delete: async (id: string) => {
    return del(`/hostels/${id}/`);
  },

  approve: async (id: string) => {
    return post(`/hostels/${id}/approve/`);
  },

  reject: async (id: string, reason?: string) => {
    return post(`/hostels/${id}/reject/`, { reason });
  },
};

// ============================================================================
// Super Admin Dashboard API
// ============================================================================

export interface DashboardUserOverview {
  student?: number | null;
  donors?: number | null;
  managements?: number | null;
  trustees?: number | null;
}

export interface DashboardData {
  total_institute: number;
  pending_approvals: number;
  approved_institution: number;
  rejected_institution: number;
  total_student: number;
  donors: number;
  total_donations: number;
  user_overview?: DashboardUserOverview | null;
}

export interface DashboardApiResponse {
  message?: string;
  data?: DashboardData;
}

export const dashboardAPI = {
  /** GET /jbf/dashboard/ — super admin dashboard stats and overview */
  get: async (): Promise<DashboardData> => {
    const res = await get<DashboardApiResponse>("/jbf/dashboard/");
    const data = res?.data;
    if (!data) {
      return {
        total_institute: 0,
        pending_approvals: 0,
        approved_institution: 0,
        rejected_institution: 0,
        total_student: 0,
        donors: 0,
        total_donations: 0,
        user_overview: { student: 0, donors: 0, managements: 0, trustees: 0 },
      };
    }
    return {
      total_institute: data.total_institute ?? 0,
      pending_approvals: data.pending_approvals ?? 0,
      approved_institution: data.approved_institution ?? 0,
      rejected_institution: data.rejected_institution ?? 0,
      total_student: data.total_student ?? 0,
      donors: data.donors ?? 0,
      total_donations: data.total_donations ?? 0,
      user_overview: data.user_overview ?? null,
    };
  },
};

// ============================================================================
// Donation & Funding Status (Super Admin) — /jbf/donation-details/ , /jbf/funding-status/
// ============================================================================

export interface DonationPayment {
  id: number;
  razorpay_order_id: string;
  razorpay_payment_id: string | null;
  razorpay_signature: string | null;
  amount: string;
  currency: string;
  status: string;
  created_at: string;
  payment_method: string;
  payment_method_details: PaymentMethodDetails | null;
  receipt_no: string;
}

export interface PaymentMethodDetails {
  vpa: string;
  email: string;
  contact: string;
}

export interface DonationDetail {
  id: number;
  name: string;
  email: string;
  phone: string;
  organization: string | null;
  donation_type: string;
  amount: string;
  pan_number: string | null;
  aadhaar_number: string | null;
  message: string | null;
  created_at: string;
  payments: DonationPayment[];
}

export interface DonationDetailsResponse {
  data?: DonationDetail[];
}

export interface FundingStatusByType {
  donation_type: string;
  amount: number;
  percentage: number;
}

export interface FundingStatusData {
  total_funding: number;
  by_type: FundingStatusByType[];
}

export interface FundingStatusResponse {
  message?: string;
  status_code?: number;
  data?: FundingStatusData;
}

export const donationAPI = {
  /** GET /jbf/donation-details/ — list all donations with payment details */
  getDonationDetails: async (): Promise<DonationDetail[]> => {
    const res = await get<DonationDetailsResponse>("/jbf/donation-details/");
    return Array.isArray(res?.data) ? res.data : [];
  },
};

export const fundingStatusAPI = {
  /** GET /jbf/donation-status/ — total funding and breakdown by donation type */
  get: async (): Promise<FundingStatusData> => {
    const res = await get<FundingStatusResponse>("/jbf/donation-status/");
    const data = res?.data;
    if (!data) {
      return { total_funding: 0, by_type: [] };
    }
    return {
      total_funding: data.total_funding ?? 0,
      by_type: data.by_type ?? [],
    };
  },
};

// ============================================================================
// Institutions API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

/** vote_info from GET /jbf/institution-approvals/{id}/ or approve response. Max 2 votes: super_admin + management OR management + management. */
export interface InstitutionVoteInfo {
  super_admin?: { approved?: boolean; rejected?: boolean };
  management?: { approved_count?: number; rejected_count?: number };
  total_approvals?: number;
  total_rejections?: number;
  total_votes?: number;
  /** When present, indicates whether the current logged-in user (e.g. management-2) has already approved. Use this to show only Reject for that user; other management users still see Approve + Reject. */
  current_user_approved?: boolean | null;
}

/** Response from POST /jbf/institution-approvals/{id}/approve/ */
export interface InstitutionApproveResponse {
  message: string;
  data: Institution & { approval_status?: string };
  vote_info?: InstitutionVoteInfo;
}

export const institutionsAPI = {
  getAll: async (params?: { status?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return get<Institution[]>(`/jbf/institutions/${queryParams}`);
  },
  getApprovalsAll: async (params?: {
    search?: string;
    approval_status?: string;
  }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return get<Institution[]>(`/jbf/institution-approvals/${queryParams}`);
  },

  /** GET /jbf/institution-approvals/{id}/ — returns institution with approval_status and vote_info */
  get: async (id: string) => {
    return get<
      Institution & {
        approval_status?: string;
        vote_info?: InstitutionVoteInfo;
      }
    >(`/jbf/institution-approvals/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Institution[]>(`/jbf/institutions/?user_id=${userId}`);
  },

  /** FormData field name for institution images in create/update payload */
  INSTITUTION_IMAGES_FIELD: "image",

  /**
   * Map frontend payload to API field names (matches curl form keys).
   * Converts selectedDepartments -> selected_departments, etc. Drops image/files.
   */
  toApiPayload: (data: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const skipKeys = ["image", "images", "existing_image_urls"];
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null || skipKeys.includes(key)) {
        continue;
      }
      if (key === "selectedDepartments") {
        out.selected_departments = Array.isArray(value) ? value : [];
      } else if (key === "selectedFacilities") {
        out.selected_facilities = Array.isArray(value) ? value : [];
      } else if (key === "selectedLevels") {
        out.selected_levels = Array.isArray(value) ? value : [];
      } else {
        out[key] = value;
      }
    }
    return out;
  },

  /**
   * Build FormData for institutions API: scalar fields as form key=value;
   * selected_departments, selected_facilities, selected_levels as repeated;
   * educational_year as JSON string.
   */
  buildInstitutionFormData: (
    data: Record<string, unknown>,
    images?: File[]
  ) => {
    const payload = institutionsAPI.toApiPayload(data);
    const formData = new FormData();
    const repeatedKeys = [
      "selected_departments",
      "selected_facilities",
      "selected_levels",
    ];
    for (const [key, value] of Object.entries(payload)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (repeatedKeys.includes(key)) {
        const arr = Array.isArray(value) ? value : [];
        arr.forEach(v => formData.append(key, String(v)));
        continue;
      }
      if (key === "educational_year") {
        formData.append(key, JSON.stringify(Array.isArray(value) ? value : []));
        continue;
      }
      if (
        typeof value === "object" &&
        value !== null &&
        !(value instanceof File)
      ) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    }
    if (images?.length) {
      images.forEach(file =>
        formData.append(institutionsAPI.INSTITUTION_IMAGES_FIELD, file)
      );
    }
    return formData;
  },

  /** POST /jbf/institutions/ — always uses FormData mapping. */
  create: async (
    data: Partial<Institution> & Record<string, unknown>,
    images?: File[]
  ) => {
    const formData = institutionsAPI.buildInstitutionFormData(
      data,
      images ?? []
    );
    return post<Institution>("/jbf/institutions/", formData, {
      headers: { "Content-Type": undefined },
    } as any);
  },

  /** PATCH /jbf/institutions/{id}/ — always uses FormData mapping. */
  update: async (
    id: string,
    data: Partial<Institution> & Record<string, unknown>
  ) => {
    const formData = institutionsAPI.buildInstitutionFormData(data, []);
    return patch<Institution>(`/jbf/institutions/${id}/`, formData, {
      headers: { "Content-Type": undefined },
    } as any);
  },

  /** PATCH with FormData when updating with or without new images. */
  updateWithImages: async (
    id: string,
    data: Partial<Institution> & Record<string, unknown>,
    images: File[]
  ) => {
    const formData = institutionsAPI.buildInstitutionFormData(
      data,
      images || []
    );
    return patch<Institution>(`/jbf/institutions/${id}/`, formData, {
      headers: { "Content-Type": undefined },
    } as any);
  },

  delete: async (id: string) => {
    return del(`/jbf/institutions/${id}/`);
  },

  approve: async (id: string) => {
    return post<InstitutionApproveResponse>(
      `/jbf/institution-approvals/${id}/approve/`
    );
  },

  reject: async (id: string, approval_notes?: string) => {
    return post(`/jbf/institution-approvals/${id}/reject/`, { approval_notes });
  },

  /** PATCH /jbf/institution-approvals/{id}/ — set is_highlighted (value: true | false). Super admin only. */
  setHighlighted: async (id: string | number, value: boolean) => {
    return patch<Institution>(
      `/jbf/institution-approvals/${id}/set-checkbox/`,
      { value }
    );
  },
};

// ============================================================================
// Donors API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const donorsAPI = {
  getAll: async () => {
    return get<Donor[]>("/donors/");
  },

  get: async (id: string) => {
    return get<Donor>(`/donors/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Donor>(`/donors/?user_id=${userId}`);
  },

  create: async (data: Partial<Donor>) => {
    return post<Donor>("/donors/", data);
  },

  getDonations: async (donorId: string) => {
    return get(`/donors/${donorId}/donations/`);
  },
};

// ============================================================================
// Trustees API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const trusteesAPI = {
  getAll: async () => {
    return get<Trustee[]>("/trustees/");
  },

  get: async (id: string) => {
    return get<Trustee>(`/trustees/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Trustee>(`/trustees/?user_id=${userId}`);
  },

  create: async (data: Partial<Trustee>) => {
    return post<Trustee>("/trustees/", data);
  },
};

// ============================================================================
// Applications API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const applicationsAPI = {
  getAll: async (params?: {
    student_id?: string;
    hostel_id?: string;
    status?: string;
  }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : "";
    return get(`/applications/${queryParams}`);
  },

  get: async (id: string) => {
    return get(`/applications/${id}/`);
  },

  create: async (data: any) => {
    return post("/applications/", data);
  },

  update: async (id: string, data: any) => {
    return patch(`/applications/${id}/`, data);
  },
};

// ============================================================================
// Donations API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const donationsAPI = {
  getAll: async (params?: { donor_id?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : "";
    return get(`/donations/${queryParams}`);
  },

  get: async (id: string) => {
    return get(`/donations/${id}/`);
  },

  create: async (data: any) => {
    return post("/donations/", data);
  },
};

// ============================================================================
// Scholarships API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const scholarshipsAPI = {
  getAll: async () => {
    return get("/scholarships/");
  },

  get: async (id: string) => {
    return get(`/scholarships/${id}/`);
  },

  create: async (data: any) => {
    return post("/scholarships/", data);
  },

  update: async (id: string, data: any) => {
    return patch(`/scholarships/${id}/`, data);
  },

  delete: async (id: string) => {
    return del(`/scholarships/${id}/`);
  },
};

// ============================================================================
// Blogs API (GET /blogs/, POST /blogs/, GET/PUT/PATCH/DELETE /blogs/{id}/)
// ============================================================================

export const blogsAPI = {
  /** GET /blogs/ — blogs_list: retrieve list of all blogs. Returns response.data. */
  list: async (params?: Record<string, string | number | boolean>) => {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null && `${value}` !== "") {
                acc[key] = String(value);
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : "";
    const res = await get<BlogListResponse>(`/jbf/blogs/${queryParams}`);
    return res?.data ?? [];
  },

  /** POST /jbf/blogs/ — blogs_create: create a new blog. Pass FormData when image is included. Content-Type omitted so axios sends multipart/form-data with boundary. */
  create: async (data: BlogCreatePayload | FormData) => {
    const formDataConfig = { headers: { "Content-Type": false } } as any;
    if (data instanceof FormData) {
      return post<Blog>("/jbf/blogs/", data, formDataConfig);
    }
    const { image, ...rest } = data;
    if (image) {
      const formData = new FormData();
      formData.append("title", rest.title);
      if (rest.sub_title !== null && rest.sub_title !== undefined) {
        formData.append("sub_title", rest.sub_title);
      }
      if (rest.description !== null && rest.description !== undefined) {
        formData.append("description", rest.description);
      }
      if (rest.category !== null && rest.category !== undefined) {
        formData.append("category", rest.category);
      }
      if (rest.date !== null && rest.date !== undefined) {
        formData.append("date", rest.date);
      }
      if (rest.is_enabled !== null && rest.is_enabled !== undefined) {
        formData.append("is_enabled", String(rest.is_enabled));
      }
      formData.append("banner", image);
      return post<Blog>("/jbf/blogs/", formData, formDataConfig);
    }
    return post<Blog>("/jbf/blogs/", rest);
  },

  /** GET /jbf/blogs/{id}/ — blogs_read: retrieve a single blog */
  get: async (id: string | number) => {
    return get<Blog>(`/jbf/blogs/${id}/`);
  },

  /** PUT /jbf/blogs/{id}/ — blogs_update: full update of a blog */
  update: async (id: string | number, data: Partial<BlogCreatePayload>) => {
    return put<Blog>(`/jbf/blogs/${id}/`, data as any);
  },

  /** PATCH /jbf/blogs/{id}/ — blogs_partial_update: partial update. Sends FormData when image is included. */
  partialUpdate: async (
    id: string | number,
    data: Partial<BlogCreatePayload>
  ) => {
    const { image, ...rest } = data;
    if (image instanceof File) {
      const formData = new FormData();
      if (rest.title !== null && rest.title !== undefined) {
        formData.append("title", rest.title);
      }
      if (rest.sub_title !== null && rest.sub_title !== undefined) {
        formData.append("sub_title", rest.sub_title);
      }
      if (rest.description !== null && rest.description !== undefined) {
        formData.append("description", rest.description);
      }
      if (rest.category !== null && rest.category !== undefined) {
        formData.append("category", rest.category);
      }
      if (rest.date !== null && rest.date !== undefined) {
        formData.append("date", rest.date);
      }
      if (rest.is_enabled !== null && rest.is_enabled !== undefined) {
        formData.append("is_enabled", String(rest.is_enabled));
      }
      formData.append("banner", image);
      return patch<Blog>(`/jbf/blogs/${id}/`, formData, {
        headers: { "Content-Type": false },
      } as any);
    }
    return patch<Blog>(`/jbf/blogs/${id}/`, rest as any);
  },

  /** DELETE /jbf/blogs/{id}/ — blogs_delete: delete a blog */
  delete: async (id: string | number) => {
    return del(`/jbf/blogs/${id}/`);
  },

  /** GET /jbf/blog-categories/ — returns list of categories (response.data) */
  categories: async () => {
    const res = await get<BlogCategoriesResponse>(`/jbf/blog-categories/`);
    return res?.data ?? [];
  },
};

// ============================================================================
// Events API (GET /jbf/events/, POST /jbf/events/, GET/PUT/PATCH/DELETE /jbf/events/{id}/)
// ============================================================================

export const eventsAPI = {
  /** GET /jbf/events/ — events_list: retrieve list of all events. */
  list: async (params?: Record<string, string | number | boolean>) => {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null && `${value}` !== "") {
                acc[key] = String(value);
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : "";
    const res = await get<EventListResponse>(`/jbf/events/${queryParams}`);
    return res?.data ?? [];
  },

  /** POST /jbf/events/ — events_create: create a new event. */
  create: async (data: EventCreatePayload) => {
    return post<JBFEvent>("/jbf/events/", data);
  },

  /** GET /jbf/events/{id}/ — events_read: retrieve a single event */
  get: async (id: string | number) => {
    return get<JBFEvent>(`/jbf/events/${id}/`);
  },

  /** PUT /jbf/events/{id}/ — events_update: full update of an event */
  update: async (id: string | number, data: Partial<EventCreatePayload>) => {
    return put<JBFEvent>(`/jbf/events/${id}/`, data);
  },

  /** PATCH /jbf/events/{id}/ — events_partial_update: partial update of an event */
  partialUpdate: async (
    id: string | number,
    data: Partial<EventCreatePayload>
  ) => {
    return patch<JBFEvent>(`/jbf/events/${id}/`, data);
  },

  /** DELETE /jbf/events/{id}/ — events_delete: delete an event */
  delete: async (id: string | number) => {
    return del(`/jbf/events/${id}/`);
  },
};

// ============================================================================
// Contact Forms API (Super Admin) — GET /jbf/contact-forms/ , GET /jbf/contact-forms/{id}/
// ============================================================================

export interface ContactForm {
  id: string | number;
  name: string;
  email: string;
  phone_number: string;
  subject: string;
  message: string;
  created_at?: string;
  status?: string;
  /** Admin reply / previous response sent to contact */
  response?: string;
  [key: string]: unknown;
}

export const contactFormsAPI = {
  /** GET /jbf/contact-forms/ — list all contact form submissions */
  list: async (): Promise<ContactForm[]> => {
    const res = await get<{ data?: ContactForm[]; results?: ContactForm[] }>(
      "/jbf/contact-forms/"
    );
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    if (Array.isArray(res?.results)) {
      return res.results;
    }
    return [];
  },
  /** GET /jbf/contact-forms/{id}/ — get single contact form */
  getById: async (id: string | number): Promise<ContactForm | null> => {
    const res = await get<ContactForm | { data?: ContactForm }>(
      `/jbf/contact-forms/${id}/`
    );
    if (res && typeof res === "object" && "data" in res && res.data) {
      return res.data as ContactForm;
    }
    return (res as ContactForm) ?? null;
  },
  /** POST /jbf/contact-form-email/{id}/send-email/ — send reply to contact */
  sendMail: async (
    id: string | number,
    payload: { email: string; response: string }
  ): Promise<void> => {
    await post(`/jbf/contact-form-email/${id}/send-email/`, payload);
  },
};

// ============================================================================
// Vendors API (Super Admin) — adjust base path when backend is ready
// ============================================================================

export const vendorsAPI = {
  list: async (): Promise<Vendor[]> => {
    try {
      const res = await get<{ data?: Vendor[] }>("/jbf/vendors/");
      return Array.isArray(res?.data) ? res.data : [];
    } catch {
      return [];
    }
  },
  create: async (data: VendorCreatePayload | FormData): Promise<Vendor> => {
    if (data instanceof FormData) {
      return post<Vendor>("/jbf/vendors/", data, {
        headers: { "Content-Type": false },
      } as any);
    }

    const hasFiles =
      data.logo_image instanceof File ||
      data.documents_uploaded instanceof File;

    if (!hasFiles) {
      const jsonPayload: Record<string, unknown> = {
        vendor_name: data.vendor_name,
      };
      if (data.vendor_category?.length) {
        jsonPayload.vendor_category = coerceIdArray(data.vendor_category);
      }
      if (data.vendor_sub_category?.length) {
        jsonPayload.vendor_sub_category = coerceIdArray(
          data.vendor_sub_category
        );
      }
      if (data.company_name) {
        jsonPayload.company_name = data.company_name;
      }
      if (data.vendor_type) {
        jsonPayload.vendor_type = data.vendor_type;
      }
      if (data.logo_image && typeof data.logo_image === "string") {
        jsonPayload.logo_image = data.logo_image;
      }
      if (data.short_description) {
        jsonPayload.short_description = data.short_description;
      }
      if (data.contact_person_name) {
        jsonPayload.contact_person_name = data.contact_person_name;
      }
      if (data.mobile_number) {
        jsonPayload.mobile_number = data.mobile_number;
      }
      if (data.email) {
        jsonPayload.email = data.email;
      }
      if (data.office_address) {
        jsonPayload.office_address = data.office_address;
      }
      if (data.city) {
        jsonPayload.city = data.city;
      }
      if (data.state) {
        jsonPayload.state = data.state;
      }
      if (data.pin_code) {
        jsonPayload.pin_code = data.pin_code;
      }
      if (data.service_areas?.length) {
        jsonPayload.service_areas = data.service_areas;
      }
      if (data.pan_india_service === true) {
        jsonPayload.pan_india_service = true;
      }
      if (data.services_offered?.length) {
        jsonPayload.services_offered = data.services_offered;
      }
      if (data.specialization) {
        jsonPayload.specialization = data.specialization;
      }
      if (
        data.years_of_experience !== null &&
        data.years_of_experience !== undefined &&
        data.years_of_experience !== ""
      ) {
        jsonPayload.years_of_experience = coerceNumeric(
          data.years_of_experience
        );
      }
      if (data.hostel_projects_experience === true) {
        jsonPayload.hostel_projects_experience = true;
      }
      if (data.past_projects !== null && data.past_projects !== undefined) {
        jsonPayload.past_projects = data.past_projects;
      }
      if (data.empanelment_status) {
        jsonPayload.empanelment_status = data.empanelment_status;
      }
      if (data.verification_status) {
        jsonPayload.verification_status = data.verification_status;
      }
      if (
        data.documents_uploaded &&
        typeof data.documents_uploaded === "string"
      ) {
        jsonPayload.documents_uploaded = data.documents_uploaded;
      }
      if (data.compliance_confirmation === true) {
        jsonPayload.compliance_confirmation = true;
      }
      if (data.admin_notes) {
        jsonPayload.admin_notes = data.admin_notes;
      }
      if (data.available_for_work === true) {
        jsonPayload.available_for_work = true;
      }
      if (data.working_days?.length) {
        jsonPayload.working_days = data.working_days;
      }
      if (data.service_timings) {
        jsonPayload.service_timings = data.service_timings;
      }
      return post<Vendor>("/jbf/vendors/", jsonPayload, {
        headers: { "Content-Type": "application/json" },
      } as any);
    }

    const formData = new FormData();
    const append = (key: string, value: string | Blob) =>
      formData.append(key, value);

    append("vendor_name", data.vendor_name);
    if (data.vendor_category?.length) {
      data.vendor_category.forEach(val =>
        formData.append("vendor_category[]", String(val))
      );
    }
    if (data.vendor_sub_category?.length) {
      data.vendor_sub_category.forEach(val =>
        formData.append("vendor_sub_category[]", String(val))
      );
    }
    if (data.category?.length) {
      data.category.forEach(val => formData.append("category[]", String(val)));
    }
    if (
      data.company_name !== null &&
      data.company_name !== undefined &&
      data.company_name !== ""
    ) {
      append("company_name", data.company_name);
    }
    if (data.vendor_type) {
      append("vendor_type", data.vendor_type);
    }
    if (
      data.short_description !== null &&
      data.short_description !== undefined &&
      data.short_description !== ""
    ) {
      append("short_description", data.short_description);
    }
    if (
      data.contact_person_name !== null &&
      data.contact_person_name !== undefined &&
      data.contact_person_name !== ""
    ) {
      append("contact_person_name", data.contact_person_name);
    }
    if (
      data.mobile_number !== null &&
      data.mobile_number !== undefined &&
      data.mobile_number !== ""
    ) {
      append("mobile_number", data.mobile_number);
    }
    if (data.email !== null && data.email !== undefined && data.email !== "") {
      append("email", data.email);
    }
    if (
      data.office_address !== null &&
      data.office_address !== undefined &&
      data.office_address !== ""
    ) {
      append("office_address", data.office_address);
    }
    if (data.city !== null && data.city !== undefined && data.city !== "") {
      append("city", data.city);
    }
    if (data.state !== null && data.state !== undefined && data.state !== "") {
      append("state", data.state);
    }
    if (
      data.pin_code !== null &&
      data.pin_code !== undefined &&
      data.pin_code !== ""
    ) {
      append("pin_code", data.pin_code);
    }
    if (data.service_areas?.length) {
      data.service_areas.forEach(val =>
        formData.append("service_areas[]", val)
      );
    }
    if (data.pan_india_service === true) {
      append("pan_india_service", "true");
    }
    if (data.services_offered?.length) {
      data.services_offered.forEach(val =>
        formData.append("services_offered[]", val)
      );
    }
    if (
      data.specialization !== null &&
      data.specialization !== undefined &&
      data.specialization !== ""
    ) {
      append("specialization", data.specialization);
    }
    if (
      data.years_of_experience !== null &&
      data.years_of_experience !== undefined &&
      data.years_of_experience !== ""
    ) {
      append("years_of_experience", String(data.years_of_experience));
    }
    if (data.hostel_projects_experience === true) {
      append("hostel_projects_experience", "true");
    }
    if (data.past_projects !== null && data.past_projects !== undefined) {
      const pp = data.past_projects;
      append(
        "past_projects",
        Array.isArray(pp) ? JSON.stringify(pp) : String(pp || "")
      );
    }
    if (
      data.empanelment_status !== null &&
      data.empanelment_status !== undefined &&
      data.empanelment_status !== ""
    ) {
      append("empanelment_status", data.empanelment_status);
    }
    if (
      data.verification_status !== null &&
      data.verification_status !== undefined &&
      data.verification_status !== ""
    ) {
      append("verification_status", data.verification_status);
    }
    if (data.compliance_confirmation === true) {
      append("compliance_confirmation", "true");
    }
    if (
      data.admin_notes !== null &&
      data.admin_notes !== undefined &&
      data.admin_notes !== ""
    ) {
      append("admin_notes", data.admin_notes);
    }
    if (data.available_for_work === true) {
      append("available_for_work", "true");
    }
    if (data.working_days?.length) {
      data.working_days.forEach(val => formData.append("working_days[]", val));
    }
    if (
      data.service_timings !== null &&
      data.service_timings !== undefined &&
      data.service_timings !== ""
    ) {
      append("service_timings", data.service_timings);
    }

    if (data.logo_image instanceof File) {
      formData.append("logo_image", data.logo_image);
    } else if (
      typeof data.logo_image === "string" &&
      data.logo_image.trim() !== ""
    ) {
      append("logo_image", data.logo_image.trim());
    }
    if (data.documents_uploaded instanceof File) {
      formData.append("documents_uploaded", data.documents_uploaded);
    } else if (
      typeof data.documents_uploaded === "string" &&
      data.documents_uploaded.trim() !== ""
    ) {
      append("documents_uploaded", data.documents_uploaded.trim());
    }

    return post<Vendor>("/jbf/vendors/", formData, {
      headers: { "Content-Type": false },
    } as any);
  },
  get: async (id: string | number) => {
    const res = await get<
      Vendor | { data?: Vendor; message?: string; status_code?: number }
    >(`/jbf/vendors/${id}/`);
    if (res && typeof res === "object" && "data" in res && res.data !== null) {
      return res.data as Vendor;
    }
    return (res as Vendor) ?? ({} as Vendor);
  },
  update: async (
    id: string | number,
    data: Partial<VendorCreatePayload>
  ): Promise<Vendor> => {
    const { logo_image, documents_uploaded, ...rest } = data;
    const hasFiles =
      logo_image instanceof File || documents_uploaded instanceof File;
    if (hasFiles) {
      const formData = new FormData();
      const append = (key: string, value: string | Blob) =>
        formData.append(key, value);
      if (rest.vendor_name !== null && rest.vendor_name !== undefined) {
        append("vendor_name", rest.vendor_name);
      }
      if (rest.vendor_category?.length) {
        rest.vendor_category.forEach(val =>
          formData.append("vendor_category[]", String(val))
        );
      }
      if (rest.vendor_sub_category?.length) {
        rest.vendor_sub_category.forEach(val =>
          formData.append("vendor_sub_category[]", String(val))
        );
      }
      if (
        rest.company_name !== null &&
        rest.company_name !== undefined &&
        rest.company_name !== ""
      ) {
        append("company_name", rest.company_name);
      }
      if (rest.vendor_type) {
        append("vendor_type", rest.vendor_type);
      }
      if (
        rest.short_description !== null &&
        rest.short_description !== undefined &&
        rest.short_description !== ""
      ) {
        append("short_description", rest.short_description);
      }
      if (
        rest.contact_person_name !== null &&
        rest.contact_person_name !== undefined &&
        rest.contact_person_name !== ""
      ) {
        append("contact_person_name", rest.contact_person_name);
      }
      if (
        rest.mobile_number !== null &&
        rest.mobile_number !== undefined &&
        rest.mobile_number !== ""
      ) {
        append("mobile_number", rest.mobile_number);
      }
      if (
        rest.email !== null &&
        rest.email !== undefined &&
        rest.email !== ""
      ) {
        append("email", rest.email);
      }
      if (
        rest.office_address !== null &&
        rest.office_address !== undefined &&
        rest.office_address !== ""
      ) {
        append("office_address", rest.office_address);
      }
      if (rest.city !== null && rest.city !== undefined && rest.city !== "") {
        append("city", rest.city);
      }
      if (
        rest.state !== null &&
        rest.state !== undefined &&
        rest.state !== ""
      ) {
        append("state", rest.state);
      }
      if (
        rest.pin_code !== null &&
        rest.pin_code !== undefined &&
        rest.pin_code !== ""
      ) {
        append("pin_code", rest.pin_code);
      }
      if (rest.service_areas?.length) {
        rest.service_areas.forEach(val =>
          formData.append("service_areas[]", val)
        );
      }
      if (rest.pan_india_service === true) {
        append("pan_india_service", "true");
      }
      if (rest.services_offered?.length) {
        rest.services_offered.forEach(val =>
          formData.append("services_offered[]", val)
        );
      }
      if (
        rest.specialization !== null &&
        rest.specialization !== undefined &&
        rest.specialization !== ""
      ) {
        append("specialization", rest.specialization);
      }
      if (
        rest.years_of_experience !== null &&
        rest.years_of_experience !== undefined &&
        rest.years_of_experience !== ""
      ) {
        append("years_of_experience", String(rest.years_of_experience));
      }
      if (rest.hostel_projects_experience === true) {
        append("hostel_projects_experience", "true");
      }
      if (rest.past_projects !== null && rest.past_projects !== undefined) {
        const pp = rest.past_projects;
        append(
          "past_projects",
          Array.isArray(pp) ? JSON.stringify(pp) : String(pp || "")
        );
      }
      if (
        rest.empanelment_status !== null &&
        rest.empanelment_status !== undefined &&
        rest.empanelment_status !== ""
      ) {
        append("empanelment_status", rest.empanelment_status);
      }
      if (
        rest.verification_status !== null &&
        rest.verification_status !== undefined &&
        rest.verification_status !== ""
      ) {
        append("verification_status", rest.verification_status);
      }
      if (rest.compliance_confirmation === true) {
        append("compliance_confirmation", "true");
      }
      if (
        rest.admin_notes !== null &&
        rest.admin_notes !== undefined &&
        rest.admin_notes !== ""
      ) {
        append("admin_notes", rest.admin_notes);
      }
      if (rest.available_for_work === true) {
        append("available_for_work", "true");
      }
      if (rest.working_days?.length) {
        rest.working_days.forEach(val =>
          formData.append("working_days[]", val)
        );
      }
      if (
        rest.service_timings !== null &&
        rest.service_timings !== undefined &&
        rest.service_timings !== ""
      ) {
        append("service_timings", rest.service_timings);
      }
      if (logo_image instanceof File) {
        formData.append("logo_image", logo_image);
      } else if (typeof logo_image === "string" && logo_image.trim() !== "") {
        append("logo_image", logo_image.trim());
      }
      if (documents_uploaded instanceof File) {
        formData.append("documents_uploaded", documents_uploaded);
      } else if (
        typeof documents_uploaded === "string" &&
        documents_uploaded.trim() !== ""
      ) {
        append("documents_uploaded", documents_uploaded.trim());
      }
      return patch<Vendor>(
        `/jbf/vendors/${id}/`,
        formData as any,
        {
          headers: { "Content-Type": false },
        } as any
      );
    }
    return patch<Vendor>(`/jbf/vendors/${id}/`, rest as any);
  },
  delete: async (id: string | number) => del(`/jbf/vendors/${id}/`),
};

// ============================================================================
// Categories (Super Admin) – /jbf/categories
// ============================================================================

export interface Category {
  id: string | number;
  name: string;
  description?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  sub_categories_count?: number;
}

export interface CategoryCreatePayload {
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface PaginationParams {
  page?: number;
  page_size?: number;
  [key: string]: any;
}

export interface PaginatedResponse<T> {
  data: T[];
  results?: T[]; // Some APIs use results instead of data
  total_pages?: number;
  total_count?: number;
  count?: number; // Some APIs use count instead of total_count
  current_page?: number;
  page_size?: number;
  message?: string;
  status_code?: number;
}

export const categoriesAPI = {
  list: async (
    params?: PaginationParams
  ): Promise<PaginatedResponse<Category> | Category[]> => {
    try {
      const queryParams = params
        ? `?${new URLSearchParams(
            Object.entries(params).reduce(
              (acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                  acc[key] = String(value);
                }
                return acc;
              },
              {} as Record<string, string>
            )
          ).toString()}`
        : "";
      const res = await get<PaginatedResponse<Category> | Category[]>(
        `/jbf/categories/${queryParams}`
      );
      return res;
    } catch {
      return [];
    }
  },
  create: async (data: CategoryCreatePayload) =>
    post<Category>("/jbf/categories/", data),
  get: async (id: string | number) => {
    const res = await get<Category | { data?: Category }>(
      `/jbf/categories/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as Category);
  },
  update: async (id: string | number, data: Partial<CategoryCreatePayload>) =>
    patch<Category>(`/jbf/categories/${id}/`, data),
  delete: async (id: string | number) => del(`/jbf/categories/${id}/`),
  /** Fetch sub-categories for given category IDs: /jbf/categories/sub-categories/?category_ids=1,2 */
  getSubCategoriesByCategoryIds: async (
    categoryIds: (string | number)[]
  ): Promise<SubCategory[]> => {
    if (!categoryIds?.length) {
      return [];
    }
    try {
      const ids = categoryIds.map(String).join(",");
      const res = await get<{ data?: SubCategory[] } | SubCategory[]>(
        `/jbf/categories/sub-categories/?category_ids=${ids}`
      );
      const list = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];
      return list;
    } catch {
      return [];
    }
  },
};

// ============================================================================
// Sub-Categories (Super Admin) – /jbf/sub-categories
// ============================================================================

export interface SubCategory {
  id: string | number;
  name: string;
  category?: string | number | Category | null;
  category_name?: string | null;
  description?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubCategoryCreatePayload {
  name: string | string[];
  category: string | number;
  description?: string;
  is_active?: boolean;
}

export const subCategoriesAPI = {
  list: async (
    params?: PaginationParams
  ): Promise<PaginatedResponse<SubCategory> | SubCategory[]> => {
    try {
      const queryParams = params
        ? `?${new URLSearchParams(
            Object.entries(params).reduce(
              (acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                  acc[key] = String(value);
                }
                return acc;
              },
              {} as Record<string, string>
            )
          ).toString()}`
        : "";
      const res = await get<PaginatedResponse<SubCategory> | SubCategory[]>(
        `/jbf/sub-categories/${queryParams}`
      );
      return res;
    } catch {
      return [];
    }
  },
  create: async (data: SubCategoryCreatePayload) =>
    post<SubCategory>("/jbf/sub-categories/", data),
  get: async (id: string | number) => {
    const res = await get<SubCategory | { data?: SubCategory }>(
      `/jbf/sub-categories/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as SubCategory);
  },
  update: async (
    id: string | number,
    data: Partial<SubCategoryCreatePayload>
  ) => patch<SubCategory>(`/jbf/sub-categories/${id}/`, data),
  delete: async (id: string | number) => del(`/jbf/sub-categories/${id}/`),
};

// ============================================================================
// List Masters (Super Admin) – Country, Zone, State, City, Area
// ============================================================================

export interface Country {
  id: string | number;
  name: string;
  code?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CountryCreatePayload {
  name: string;
  code?: string;
  is_active?: boolean;
}

export interface Zone {
  id: string | number;
  name: string;
  country?: string | number | null;
  country_name?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ZoneCreatePayload {
  name: string;
  country: string | number;
  is_active?: boolean;
}

export interface State {
  id: string | number;
  name: string;
  country?: string | number | null;
  country_name?: string | null;
  zone?: string | number | null;
  zone_name?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface StateCreatePayload {
  name: string;
  /** Country (legacy) or zone – use zone when creating state under a zone */
  country?: string | number;
  zone?: string | number;
  is_active?: boolean;
}

export interface City {
  id: string | number;
  name: string;
  state?: string | number | null;
  state_name?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CityCreatePayload {
  name: string;
  state: string | number;
  is_active?: boolean;
}

export interface Area {
  id: string | number;
  name: string;
  city?: string | number | null;
  city_name?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AreaCreatePayload {
  name: string;
  city: string | number;
  is_active?: boolean;
}

function listMasterList<T>(res: unknown, fallback: T[]): T[] {
  if (Array.isArray(res)) {
    return res;
  }
  if (
    res &&
    typeof res === "object" &&
    "data" in res &&
    Array.isArray((res as { data: T[] }).data)
  ) {
    return (res as { data: T[] }).data;
  }
  return fallback;
}

export const countriesAPI = {
  list: async (): Promise<Country[]> => {
    try {
      const res = await get<{ data?: Country[] } | Country[]>(
        "/jbf/countries/"
      );
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
  create: (data: CountryCreatePayload) =>
    post<Country>("/jbf/countries/", data),
  get: async (id: string | number) => {
    const res = await get<Country | { data?: Country }>(
      `/jbf/countries/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as Country);
  },
  update: (id: string | number, data: Partial<CountryCreatePayload>) =>
    patch<Country>(`/jbf/countries/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/countries/${id}/`),
  /** Zones by country: GET /jbf/countries/zones/?country_id=1 */
  getZonesByCountryId: async (countryId: string | number): Promise<Zone[]> => {
    try {
      const res = await get<{ data?: Zone[] } | Zone[]>(
        `/jbf/countries/zones/?country_id=${countryId}`
      );
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
};

export const zonesAPI = {
  list: async (): Promise<Zone[]> => {
    try {
      const res = await get<{ data?: Zone[] } | Zone[]>("/jbf/zones/");
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
  create: (data: ZoneCreatePayload) => post<Zone>("/jbf/zones/", data),
  get: async (id: string | number) => {
    const res = await get<Zone | { data?: Zone }>(`/jbf/zones/${id}/`);
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as Zone);
  },
  update: (id: string | number, data: Partial<ZoneCreatePayload>) =>
    patch<Zone>(`/jbf/zones/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/zones/${id}/`),
  /** States by zone: GET /jbf/zones/states/?zone_id=1 */
  getStatesByZoneId: async (zoneId: string | number): Promise<State[]> => {
    try {
      const res = await get<{ data?: State[] } | State[]>(
        `/jbf/zones/states/?zone_id=${zoneId}`
      );
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
};

export const statesAPI = {
  list: async (): Promise<State[]> => {
    try {
      const res = await get<{ data?: State[] } | State[]>("/jbf/states/");
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
  create: (data: StateCreatePayload) => post<State>("/jbf/states/", data),
  get: async (id: string | number) => {
    const res = await get<State | { data?: State }>(`/jbf/states/${id}/`);
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as State);
  },
  update: (id: string | number, data: Partial<StateCreatePayload>) =>
    patch<State>(`/jbf/states/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/states/${id}/`),
  /** Cities by state: GET /jbf/states/cities/?state_id=1 */
  getCitiesByStateId: async (stateId: string | number): Promise<City[]> => {
    try {
      const res = await get<{ data?: City[] } | City[]>(
        `/jbf/states/cities/?state_id=${stateId}`
      );
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
};

export const citiesAPI = {
  list: async (): Promise<City[]> => {
    try {
      const res = await get<{ data?: City[] } | City[]>("/jbf/cities/");
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
  create: (data: CityCreatePayload) => post<City>("/jbf/cities/", data),
  get: async (id: string | number) => {
    const res = await get<City | { data?: City }>(`/jbf/cities/${id}/`);
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as City);
  },
  update: (id: string | number, data: Partial<CityCreatePayload>) =>
    patch<City>(`/jbf/cities/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/cities/${id}/`),
  /** Areas by city: GET /jbf/cities/areas/?city_id=1 */
  getAreasByCityId: async (cityId: string | number): Promise<Area[]> => {
    try {
      const res = await get<{ data?: Area[] } | Area[]>(
        `/jbf/cities/areas/?city_id=${cityId}`
      );
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
};

export const areasAPI = {
  list: async (): Promise<Area[]> => {
    try {
      const res = await get<{ data?: Area[] } | Area[]>("/jbf/areas/");
      return listMasterList(res, []);
    } catch {
      return [];
    }
  },
  create: (data: AreaCreatePayload) => post<Area>("/jbf/areas/", data),
  get: async (id: string | number) => {
    const res = await get<Area | { data?: Area }>(`/jbf/areas/${id}/`);
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as Area);
  },
  update: (id: string | number, data: Partial<AreaCreatePayload>) =>
    patch<Area>(`/jbf/areas/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/areas/${id}/`),
};

// ============================================================================
// FAQs API
// ============================================================================

export const faqCategoriesAPI = {
  list: async (): Promise<FAQCategory[]> => {
    try {
      const res = await get<{ data?: FAQCategory[] } | FAQCategory[]>(
        "/jbf/faq-categories/"
      );
      if (Array.isArray(res)) {
        return res;
      }
      if (
        res &&
        typeof res === "object" &&
        "data" in res &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  create: (data: FAQCategoryCreatePayload) =>
    post<FAQCategory>("/jbf/faq-categories/", data),
  get: async (id: string | number) => {
    const res = await get<FAQCategory | { data?: FAQCategory }>(
      `/jbf/faq-categories/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as FAQCategory);
  },
  update: (id: string | number, data: Partial<FAQCategoryCreatePayload>) =>
    patch<FAQCategory>(`/jbf/faq-categories/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/faq-categories/${id}/`),
};

export const faqQuestionsAPI = {
  list: async (): Promise<FAQQuestion[]> => {
    try {
      const res = await get<{ data?: FAQQuestion[] } | FAQQuestion[]>(
        "/jbf/faq-questions/"
      );
      if (Array.isArray(res)) {
        return res;
      }
      if (
        res &&
        typeof res === "object" &&
        "data" in res &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  create: (data: FAQQuestionCreatePayload) =>
    post<FAQQuestion>("/jbf/faq-questions/", data),
  get: async (id: string | number) => {
    const res = await get<FAQQuestion | { data?: FAQQuestion }>(
      `/jbf/faq-questions/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as FAQQuestion);
  },
  update: (id: string | number, data: Partial<FAQQuestionCreatePayload>) =>
    patch<FAQQuestion>(`/jbf/faq-questions/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/faq-questions/${id}/`),
};

// ============================================================================
// Testimonials API
// ============================================================================

export const testimonialsAPI = {
  list: async (): Promise<Testimonial[]> => {
    try {
      const res = await get<{ data?: Testimonial[] } | Testimonial[]>(
        "/jbf/testimonials/"
      );
      if (Array.isArray(res)) {
        return res;
      }
      if (
        res &&
        typeof res === "object" &&
        "data" in res &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  create: (data: TestimonialCreatePayload) =>
    post<Testimonial>("/jbf/testimonials/", data),
  get: async (id: string | number) => {
    const res = await get<Testimonial | { data?: Testimonial }>(
      `/jbf/testimonials/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as Testimonial);
  },
  update: (id: string | number, data: Partial<TestimonialCreatePayload>) =>
    patch<Testimonial>(`/jbf/testimonials/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/testimonials/${id}/`),
};

// ============================================================================
// Statistics/Dashboard API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const statsAPI = {
  getDashboardStats: async () => {
    return get("/stats/dashboard/");
  },

  getHostelStats: async (hostelId?: string) => {
    const query = hostelId ? `?hostel_id=${hostelId}` : "";
    return get(`/stats/hostels/${query}`);
  },

  getInstitutionStats: async (institutionId?: string) => {
    const query = institutionId ? `?institution_id=${institutionId}` : "";
    return get(`/stats/institutions/${query}`);
  },
};

// ============================================================================
// Media Gallery API
// ============================================================================

export const galleryCategoriesAPI = {
  list: async (): Promise<GalleryCategory[]> => {
    try {
      const res = await get<{ data?: GalleryCategory[] } | GalleryCategory[]>(
        "/jbf/gallery-categories/"
      );
      if (Array.isArray(res)) {
        return res;
      }
      if (
        res &&
        typeof res === "object" &&
        "data" in res &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  create: (data: GalleryCategoryCreatePayload) =>
    post<GalleryCategory>("/jbf/gallery-categories/", data),
  get: async (id: string | number) => {
    const res = await get<GalleryCategory | { data?: GalleryCategory }>(
      `/jbf/gallery-categories/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as GalleryCategory);
  },
  update: (id: string | number, data: Partial<GalleryCategoryCreatePayload>) =>
    patch<GalleryCategory>(`/jbf/gallery-categories/${id}/`, data),
  delete: (id: string | number) => del(`/jbf/gallery-categories/${id}/`),
};

export const galleryAPI = {
  list: async (): Promise<GalleryItem[]> => {
    try {
      const res = await get<{ data?: GalleryItem[] } | GalleryItem[]>(
        "/jbf/gallery/"
      );
      if (Array.isArray(res)) {
        return res;
      }
      if (
        res &&
        typeof res === "object" &&
        "data" in res &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  create: (data: FormData) =>
    post<GalleryItem>("/jbf/gallery/", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  get: async (id: string | number) => {
    const res = await get<GalleryItem | { data?: GalleryItem }>(
      `/jbf/gallery/${id}/`
    );
    return res && typeof res === "object" && "data" in res && res.data
      ? res.data
      : (res as GalleryItem);
  },
  update: (id: string | number, data: FormData) =>
    patch<GalleryItem>(`/jbf/gallery/${id}/`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id: string | number) => del(`/jbf/gallery/${id}/`),
};
